import PT from 'prop-types';

import { DateInput } from '~/eds/src/components/DateInput/DateInput';
import { Select } from '~/eds/src/components/Select/Select';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { parseUserDate } from '~/i18n/utils';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import BooleanSelect from '~/ui/components/BooleanSelect/BooleanSelect';
import Box from '~/ui/components/Box/Box';
import UiDateInput from '~/ui/components/DateInput/DateInput';
import NumberInput from '~/ui/components/NumberInput/NumberInput';
import TextInput from '~/ui/components/TextInput/TextInput';

import DepartmentSelect from '../DepartmentSelect/DepartmentSelect';
import UserSelect from '../UserSelect/UserSelect';
import dataTypes from './dataTypes';

function SingleValueInput({
  dataType,
  disabled,
  error,
  name,
  options = [],
  value,
  onChange,
}) {
  const enableLocalization = useFlag(FlagType.LanguageSelect);
  const formatDate = useFormatDate();
  let Input;
  switch (dataType) {
    case dataTypes.BOOLEAN:
      Input = (
        <BooleanSelect
          disabled={disabled}
          error={error}
          name={name}
          placeholder="..."
          value={value}
          width="100%"
          onChange={onChange}
        />
      );
      break;
    case dataTypes.DATE:
      Input = enableLocalization ? (
        <DateInput
          disabled={disabled}
          error={error}
          name={name}
          value={value ? parseUserDate(value) : null}
          width="input.xs.width"
          onChange={(date) => (date ? onChange(formatDate(date, 'iso')) : null)}
        />
      ) : (
        <UiDateInput
          disabled={disabled}
          error={error}
          name={name}
          placeholder="..."
          placement="bottom-end"
          value={value}
          width="fullWidth"
          onChange={onChange}
        />
      );
      break;
    case dataTypes.DEPARTMENT:
      Input = (
        <DepartmentSelect
          enableErrorMessage
          enablePortal
          disabled={disabled}
          error={error}
          name={name}
          placeholder="..."
          value={value ?? null}
          width="fullWidth"
          onChange={onChange}
        />
      );
      break;
    case dataTypes.NUMBER:
      Input = (
        <NumberInput
          disableErrorMessage
          disabled={disabled}
          error={error}
          name={name}
          placeholder="..."
          value={value}
          width="fullWidth"
          onChange={onChange}
        />
      );
      break;
    case dataTypes.MULTI_SELECT:
    case dataTypes.SINGLE_SELECT:
      const isValidOption = Array.from(
        options.map((item) => item.value),
      ).includes(value);
      Input = (
        <Select
          enableErrorMessage
          enablePortal
          disabled={disabled}
          error={isValidOption ? error : 'Provide a value'}
          isMulti={false}
          name={name}
          options={options}
          placeholder="..."
          value={isValidOption ? value : '...'}
          width="100%"
          onChange={onChange}
        />
      );
      break;
    case dataTypes.TEXT:
      Input = (
        <TextInput
          disableErrorMessage
          disabled={disabled}
          error={error}
          name={name}
          placeholder="..."
          value={value}
          width="fullWidth"
          onChange={onChange}
        />
      );
      break;
    case dataTypes.USER:
      Input = (
        <UserSelect
          enablePortal
          disabled={disabled}
          error={error}
          name={name}
          placeholder="..."
          value={value}
          onChange={onChange}
        />
      );
      break;
    default:
      Input = null;
  }

  return <Box sx={{ flex: '1 1 auto' }}>{Input}</Box>;
}

SingleValueInput.propTypes = {
  dataType: PT.oneOf(Object.keys(dataTypes)).isRequired,
  disabled: PT.bool,
  error: PT.string,
  options: PT.arrayOf(
    PT.shape({ label: PT.string.isRequired, value: PT.string.isRequired })
      .isRequired,
  ),
  value: PT.any,
  onChange: PT.func.isRequired,
};

export default SingleValueInput;
